import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { jobs as jobsApi, seekAds, tokens } from '../utils/api';
import JobCard from '../components/JobCard';
import SeekAdCard from '../components/SeekAdCard';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import './Home.css';

const categories = [
  { key: 'Technical', icon: '💻' },
  { key: 'Domestic',  icon: '🏠' },
  { key: 'Freelance', icon: '🎯' },
  { key: 'Tutor',     icon: '📚' },
  { key: 'Creative',  icon: '🎨' },
  { key: 'Local Task',icon: '📦' },
];

export default function Home() {
  const { t, user } = useApp();
  const navigate     = useNavigate();
  const [search,    setSearch]    = useState('');
  const [mode,      setMode]      = useState('jobs');   // 'jobs' | 'hire'
  const [listings,  setListings]  = useState([]);
  const [seekers,   setSeekers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [appliedJobIds, setAppliedJobIds] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({ message: '', contact_info: '', experience_proof: '' });
  const [applying, setApplying] = useState(false);
  const { fetchBalance, walletBalance } = useApp();

  useEffect(() => {
    Promise.all([
      jobsApi.list({ limit: 6 }),
      seekAds.list({ perPage: 4 }),
    ]).then(([jRes, sRes]) => {
      setListings(jRes.jobs ?? []);
      setSeekers(sRes.seekAds ?? []);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = e => {
    e.preventDefault();
    if (mode === 'hire') navigate(`/find-hire?q=${encodeURIComponent(search)}`);
    else navigate(`/jobs?q=${encodeURIComponent(search)}`);
  };

  const handleApply = job => {
    if (!user) { navigate('/login'); return; }
    setSelectedJob(job);
    setShowApplyModal(true);
  };

  const confirmApply = async e => {
    if (e) e.preventDefault();
    if (!selectedJob) return;
    setApplying(true);
    try {
      await jobsApi.apply(selectedJob.id, applyForm);
      fetchBalance();
      toast.success(t('applied_success'));
      setAppliedJobIds(prev => [...prev, selectedJob.id]);
      setShowApplyModal(false);
      setApplyForm({ message: '', contact_info: '', experience_proof: '' });
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="home-page">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-bg-pattern" aria-hidden="true" />
        <div className="container hero-content">
          <div className="hero-badge fade-in">🇧🇩 Bangladesh's #1 work marketplace</div>
          <h1 className="hero-title fade-in">{t('hero_title')}</h1>
          <p className="hero-subtitle fade-in">{t('hero_subtitle')}</p>

          {/* Search mode toggle */}
          <div className="hero-mode-toggle fade-in">
            <button className={`mode-btn${mode === 'jobs' ? ' active' : ''}`} onClick={() => setMode('jobs')}>
              🔍 {t('find_job')}
            </button>
            <button className={`mode-btn${mode === 'hire' ? ' active' : ''}`} onClick={() => setMode('hire')}>
              🤝 {t('find_hire')}
            </button>
          </div>

          <form className="hero-search fade-in" onSubmit={handleSearch}>
            <div className="search-bar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="search-icon">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={mode === 'hire' ? t('search_hire_placeholder') : t('search_placeholder')}
                className="search-input" />
              <button type="submit" className="btn btn-primary search-btn">{t('search_btn')}</button>
            </div>
          </form>

          <div className="hero-stats fade-in">
            <div className="hero-stat"><strong>500+</strong><span>Active jobs</span></div>
            <div className="hero-stat-divider" />
            <div className="hero-stat"><strong>200+</strong><span>Seekers available</span></div>
            <div className="hero-stat-divider" />
            <div className="hero-stat"><strong>1,200+</strong><span>Workers</span></div>
            <div className="hero-stat-divider" />
            <div className="hero-stat"><strong>৳2L+</strong><span>Paid out</span></div>
          </div>
        </div>
      </section>

      {/* ── Categories ───────────────────────────────────────────────────── */}
      <section className="section container">
        <h2 className="section-title">{t('browse_categories')}</h2>
        <div className="categories-grid stagger">
          {categories.map(cat => (
            <Link key={cat.key}
              to={mode === 'hire'
                ? `/find-hire?category=${encodeURIComponent(cat.key)}`
                : `/jobs?category=${encodeURIComponent(cat.key)}`}
              className="cat-card">
              <span className="cat-icon">{cat.icon}</span>
              <span className="cat-label">{t(`cat_${cat.key.toLowerCase().replace(' ','_')}`) || cat.key}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Latest Jobs ──────────────────────────────────────────────────── */}
      <section className="section container">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <h2 className="section-title" style={{ marginBottom:0 }}>{t('latest_listings')}</h2>
          <Link to="/jobs" className="btn btn-outline btn-sm">View all →</Link>
        </div>
        {loading ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : listings.length === 0 ? (
          <div className="empty-state">{t('no_jobs_found')}</div>
        ) : (
          <div className="stagger">
            {listings.map(job => (
              <JobCard 
                key={job.id} 
                job={job} 
                onApply={handleApply} 
                applied={appliedJobIds.includes(job.id)} 
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Available Seekers ─────────────────────────────────────────────── */}
      <section className="section container">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div>
            <h2 className="section-title" style={{ marginBottom:2 }}>{t('latest_seekers')}</h2>
            <p style={{ fontSize:'13px', color:'var(--text3)' }}>Job seekers who have published their availability — hire them directly</p>
          </div>
          <Link to="/find-hire" className="btn btn-outline btn-sm">Browse all →</Link>
        </div>
        {loading ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : seekers.length === 0 ? (
          <div className="empty-state">{t('no_seekers_found')}</div>
        ) : (
          <div className="stagger">
            {seekers.map(ad => <SeekAdCard key={ad.id} ad={ad} showActions={!!user} />)}
          </div>
        )}
      </section>

      {/* ── Seeker CTA (for seekers only) ────────────────────────────────── */}
      {user?.role === 'SEEKER' && (
        <section className="section container">
          <div className="card" style={{ background:'linear-gradient(135deg,#1D9E75,#0F6E56)', border:'none', padding:'28px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
            <div>
              <div style={{ fontSize:'18px', fontWeight:700, color:'#fff', marginBottom:'6px' }}>Let employers find you</div>
              <div style={{ fontSize:'14px', color:'rgba(255,255,255,0.8)' }}>Publish your profile, skills, and hire rate so employers come to you.</div>
            </div>
            <Link to="/post-seek-ad" className="btn btn-lg" style={{ background:'rgba(255,255,255,0.2)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', flexShrink:0 }}>
              + {t('post_seek')}
            </Link>
          </div>
        </section>
      )}

      {/* ── Guest CTA ────────────────────────────────────────────────────── */}
      {!user && (
        <section className="cta-section">
          <div className="container cta-inner">
            <div>
              <h2 style={{ fontSize:'22px', fontWeight:700, marginBottom:'6px' }}>Ready to get started?</h2>
              <p style={{ color:'var(--text2)', fontSize:'14px' }}>Join thousands of workers and employers on KajerBazar.</p>
            </div>
            <div style={{ display:'flex', gap:'10px', flexShrink:0 }}>
              <Link to="/register" className="btn btn-primary btn-lg">{t('nav_register')}</Link>
              <Link to="/jobs" className="btn btn-outline btn-lg">{t('nav_find_job')}</Link>
            </div>
          </div>
        </section>
      )}
      {/* Apply Modal */}
      <Modal open={showApplyModal} onClose={() => setShowApplyModal(false)} title={t('apply_for_job')}>
        <div style={{ padding: '4px' }}>
          <div className="form-group">
            <label className="form-label">{t('application_message')} *</label>
            <textarea 
              className="form-input" 
              rows={4} 
              required
              value={applyForm.message}
              onChange={e => setApplyForm({ ...applyForm, message: e.target.value })}
              placeholder="Why are you the best fit for this job?" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('contact_info')} (Optional)</label>
            <input 
              className="form-input" 
              value={applyForm.contact_info}
              onChange={e => setApplyForm({ ...applyForm, contact_info: e.target.value })}
              placeholder="Mobile no. or alternative email" 
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('experience_proof')} (Optional URL/Text)</label>
            <textarea 
              className="form-input" 
              rows={2}
              value={applyForm.experience_proof}
              onChange={e => setApplyForm({ ...applyForm, experience_proof: e.target.value })}
              placeholder="Link to portfolio, CV or past work examples" 
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button className="btn btn-outline btn-full" onClick={() => setShowApplyModal(false)}>{t('cancel')}</button>
            <button className="btn btn-primary btn-full" onClick={confirmApply} disabled={applying || !applyForm.message}>
              {applying ? t('submitting') : `${t('submit')} (−1 token)`}
            </button>
          </div>
          <p style={{ fontSize: '11px', color: 'var(--text3)', textAlign: 'center', marginTop: '12px' }}>
            Applying costs 1 token. You have {walletBalance ?? '...'} tokens.
          </p>
        </div>
      </Modal>
    </div>
  );
}
